import {
    RiSendPlaneLine,
} from "@remixicon/react";
import React, { useState } from "react";
import {
    Article,
    ArticleType,
    GetArticleDocument,
    ListArticleVersionsDocument,
    usePublishArticleMutation,
} from "../../../__generated__/api";
import { useConfirm } from "../../../components/confirm/Confirm";
import DateCalendarPicker from "../../../components/pickers/date-range/DateCalendarPicker";
import Button from "../../../components/theme/button/Button";
import Toggle from "../../../components/theme/toggle/Toggle";
import Styles from "./AnswerDetails.module.scss";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

export enum PublishType {
    NOW = "NOW",
    LATER = "LATER",
    KNOWLEDGE = "KNOWLEDGE",
}

export function PublishItem({
    onChange, canPublishKnowledge,
}: {
    onChange: (type: PublishType, date: Date | undefined) => void;
    canPublishKnowledge: boolean | undefined;
}) {
    const [publishedType, setPublishedType] = useState(PublishType.NOW);
    const [date, setDate] = useState<Date>();

    return (
        <div>
            Publishing an article will make it visible to all the users in the portal. Do you want to publish the article now,
            or schedule it for later?
            <div className={Styles["publish-article"]}>
                <div>
                    <Toggle
                        variant="radio"
                        text="Publish Now"
                        checked={publishedType === PublishType.NOW}
                        onChange={() => {
                            setPublishedType(PublishType.NOW);
                            onChange(PublishType.NOW, undefined);
                        }}
                    />
                </div>
                <div>
                    <Toggle
                        variant="radio"
                        text="Publish Later"
                        checked={publishedType === PublishType.LATER}
                        onChange={() => {
                            setPublishedType(PublishType.LATER);
                            onChange(PublishType.LATER, undefined);
                        }}
                    />
                    {publishedType === PublishType.LATER && (
                        <DateCalendarPicker
                            calendarClassName={Styles["publish-article-date"]}
                            value={date}
                            onChange={value => {
                                onChange(PublishType.LATER, value);
                                setDate(value);
                            }}
                            allowTime
                            allowNewTime
                            shouldShowDone={false}
                        />
                    )}
                </div>
                {canPublishKnowledge && (
                    <div>
                        <Toggle
                            variant="radio"
                            text="Publish Knowledge"
                            checked={publishedType === PublishType.KNOWLEDGE}
                            onChange={() => {
                                setPublishedType(PublishType.KNOWLEDGE);
                                onChange(PublishType.KNOWLEDGE, undefined);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function PublishButton({
    article,
    canPublish,
    onPublish,
    canPublishKnowledge,
}: {
    article: Article | undefined | null,
    canPublish: boolean;
    onPublish?: (type: PublishType) => void;
    canPublishKnowledge?: boolean;
}) {
    const [publish] = usePublishArticleMutation();
    const [publishDate, setPublishDate] = useState<Date>();
    const [publishType, setPublishType] = useState<PublishType>(PublishType.NOW);
    const { element: publishElement, setShouldShow: setShowPublish } = useConfirm({
        title: "Publish Article",
        confirmText: "Publish",
        confirmDisabledReason: !canPublish ? "Article saving... Give us a few seconds and try again." : undefined,
        closeText: "Cancel",
        confirmMetricPath: [LoggingHandler.Metrics.ARTICLES, "publish"],
        onConfirm: async () => {
            await publish({
                refetchQueries: [
                    { query: GetArticleDocument, variables: { id: article?.id } },
                    { query: ListArticleVersionsDocument, variables: { id: article?.id } },
                ],
                variables: {
                    id: article?.id,
                    settings: publishDate || publishType === PublishType.KNOWLEDGE ? {
                        publishDate,
                        type: publishType === PublishType.KNOWLEDGE ? ArticleType.Knowledge : ArticleType.Article,
                    } : undefined,
                },
            });
            onPublish?.(publishType);
        },
        body: (
            <PublishItem
                onChange={(type, date) => {
                    setPublishDate(date);
                    setPublishType(type);
                }}
                canPublishKnowledge={canPublishKnowledge}
            />
        ),
    });

    return (
        <>
            {publishElement}
            <Button
                disabled={article?.draftCreatorId == null && article?.publishedDate !== null}
                prefixIcon={<RiSendPlaneLine />}
                variant="primary"
                size="small"
                onClick={() => setShowPublish(true)}
            >
                Publish
            </Button>
        </>
    );
}

PublishButton.defaultProps = {
    onPublish: undefined,
    canPublishKnowledge: false,
};
